import React, { FC, useState, useCallback } from "react";
import { CSSTransition } from "react-transition-group";
import "./PrintErrorToast.css";
import closeCircleIcon from "../../areas/cashless/nominalCashless/close-circle-line.svg";
import { Operator } from "domains/admin/aggregates/operator/Operator";
import { PassKeyboard } from "components/passKeyboard/PassKeyboard";

interface FichaInfo {
  label: string;
  index: number;
  total: number;
}

interface PrintErrorToastProps {
  isOpen: boolean;
  onRetry: () => void;
  onSkip: () => void;
  message?: string;
  operators?: Operator[];
  /** Ficha que falhou (pedido atual no topo da fila). */
  currentFicha?: FichaInfo | null;
  /** Próxima ficha do mesmo pedido, se houver, pra oferecer a opção de avançar. */
  nextFicha?: FichaInfo | null;
}

export const PrintErrorToast: FC<PrintErrorToastProps> = ({
  isOpen,
  onRetry,
  onSkip,
  message = "Erro na impressão. Por favor, solicite suporte.",
  operators = [],
  currentFicha = null,
  nextFicha = null,
}) => {
  const nodeRef = React.useRef<HTMLDivElement | null>(null);
  const [showAuth, setShowAuth] = useState(false);
  const [showFichaSelect, setShowFichaSelect] = useState(false);
  const [operatorId, setOperatorId] = useState<string>("");
  const [authError, setAuthError] = useState(false);

  const hasFichaSelector = !!currentFicha && !!nextFicha;

  const handleStartClick = useCallback(() => {
    setShowAuth(true);
    setAuthError(false);
    // Se houver apenas um operador, já o seleciona automaticamente
    if (operators.length === 1) {
      setOperatorId(operators[0].id);
    } else {
      setOperatorId("");
    }
  }, [operators]);

  const handleCancelAuth = useCallback(() => {
    setShowAuth(false);
    setShowFichaSelect(false);
    setOperatorId("");
    setAuthError(false);
  }, []);

  const handleValidate = useCallback(
    (valid: boolean) => {
      if (valid) {
        setShowAuth(false);
        setOperatorId("");
        setAuthError(false);
        // Senha confirmada: só agora decide qual ficha tratar.
        setShowFichaSelect(true);
      } else {
        setAuthError(true);
      }
    },
    []
  );

  const handleChooseCurrentFicha = useCallback(() => {
    setShowFichaSelect(false);
    onRetry();
  }, [onRetry]);

  const handleChooseNextFicha = useCallback(() => {
    setShowFichaSelect(false);
    onSkip();
  }, [onSkip]);

  const handleBackFromFichaSelect = useCallback(() => {
    setShowFichaSelect(false);
  }, []);

  const selectedOperator = operators.find((op) => op.id === operatorId);

  return (
    <CSSTransition
      mountOnEnter
      unmountOnExit
      in={isOpen}
      timeout={500}
      classNames="print-error-modal"
      nodeRef={nodeRef}
    >
      <div className="PrintErrorToast" ref={nodeRef}>
        {!showAuth && !showFichaSelect ? (
          <div className="message-PrintErrorToast">
            <div className="icon-PrintErrorToast">
              <img src={closeCircleIcon} alt="Erro" />
            </div>
            <div className="text-PrintErrorToast">
              <span className="title">{message}</span>
              <span className="subtitle">
                Confirme a senha do operador para continuar
              </span>
            </div>

            <div className="buttons-PrintErrorToast">
              <button className="retry-button" onClick={handleStartClick}>
                Tentar novamente
              </button>
            </div>
          </div>
        ) : showFichaSelect ? (
          <div className="message-PrintErrorToast">
            <div className="text-PrintErrorToast">
              <span className="title">
                Selecione a partir de qual ficha você pretende reimprimir
              </span>
            </div>
            <div className="ficha-observation-PrintErrorToast">
              <p>Solicite o cliente e verifique as fichas já impressas.</p>
              <p>As fichas serão registradas como reimpressão.</p>
            </div>
            <div className="buttons-PrintErrorToast">
              <button className="retry-button" onClick={handleChooseCurrentFicha}>
                Ficha com erro
                {currentFicha
                  ? ` · ${currentFicha.index + 1}/${currentFicha.total} — ${currentFicha.label}`
                  : ""}
              </button>
              {hasFichaSelector ? (
                <button className="skip-button" onClick={handleChooseNextFicha}>
                  Próxima ficha &middot; {nextFicha!.index + 1}/{nextFicha!.total} &mdash; {nextFicha!.label}
                </button>
              ) : (
                <button className="skip-button" onClick={handleBackFromFichaSelect}>
                  Voltar
                </button>
              )}
            </div>
          </div>
        ) : !selectedOperator ? (
          <div className="message-PrintErrorToast">
            <div className="text-PrintErrorToast">
              <span className="title">Selecione o operador</span>
            </div>
            <div className="auth-PrintErrorToast">
              <div className="operator-list-PrintErrorToast">
                {operators.map((op) => (
                  <button
                    key={op.id}
                    className="operator-item-PrintErrorToast"
                    onClick={() => {
                      setOperatorId(op.id);
                      setAuthError(false);
                    }}
                  >
                    {op.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="buttons-PrintErrorToast">
              <button className="skip-button" onClick={handleCancelAuth}>
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div className="message-PrintErrorToast">
            <div className="text-PrintErrorToast">
              <span className="title">{selectedOperator.name}</span>
              <span className="subtitle">Digite sua senha</span>
            </div>
            {authError && (
              <span className="auth-error-PrintErrorToast">
                Senha incorreta. Tente novamente.
              </span>
            )}
            <div className="keyboard-wrapper-PrintErrorToast">
              <PassKeyboard
                password={selectedOperator.password ?? "xxx"}
                onValidate={handleValidate}
              />
            </div>
            <div className="buttons-PrintErrorToast">
              <button className="skip-button" onClick={() => { setOperatorId(""); setAuthError(false); }}>
                Voltar
              </button>
            </div>
          </div>
        )}
      </div>
    </CSSTransition>
  );
};
